/* eslint-disable react/no-this-in-sfc */
import format from "date-fns/format";
import Highcharts from "highcharts";
import { HighchartsReactRefObject } from "highcharts-react-official";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useAppVariant } from "@jugl-web/utils";
import { WidgetTooltip } from "../WidgetTooltip";
import worldMap from "./assets/world-map.topo.json";
import { ChatsByCountriesDataEntry, CountryDetails } from "./types";
import {
  countryCodeToFlagEmoji,
  extractCountryDetailsFromPoint,
} from "./utils";

export const useChatsByCountriesMap = (data: ChatsByCountriesDataEntry[]) => {
  const [tableRows, setTableRows] = useState<CountryDetails[]>([]);
  const [activeCountryKey, setActiveCountryKey] = useState<string | null>(null);

  const { isMobile } = useAppVariant();

  const chartRef = useRef<HighchartsReactRefObject>(null);

  const options = useMemo<Highcharts.Options>(
    () => ({
      chart: {
        map: worldMap,
        backgroundColor: "#FEFFFF",
        spacing: [0, 0, 0, 0],
      },
      mapView: {
        projection: {
          name: "Miller",
        },
      },
      title: {
        text: undefined,
      },
      mapNavigation: {
        enabled: isMobile,
        enableTouchZoom: isMobile,
        enableButtons: false,
      },
      navigation: {
        buttonOptions: {
          enabled: false,
        },
      },
      tooltip: {
        useHTML: true,
        followPointer: false,
        followTouchMove: false,
        backgroundColor: "transparent",
        borderWidth: 0,
        shadow: false,
        padding: 0,
        style: { whiteSpace: "normal" },
        formatter() {
          const countryDetails = extractCountryDetailsFromPoint(this);

          if (!countryDetails) {
            return "";
          }

          const flagEmoji = countryCodeToFlagEmoji(countryDetails.countryKey);

          return ReactDOMServer.renderToStaticMarkup(
            <WidgetTooltip
              title={
                <div>
                  {flagEmoji && (
                    <span className="relative top-0.5 mr-1 text-[18px]">
                      {flagEmoji}
                    </span>
                  )}
                  <span>{countryDetails.countryName}</span>
                </div>
              }
              subtitle={format(new Date(), "MMMM, yyyy")}
              content={
                <div>
                  <span className="text-dark text-base font-semibold">
                    {countryDetails.chatsCount}
                  </span>{" "}
                  <span className="text-dark-900 font-sm font-normal">
                    {countryDetails.chatsCount === 1 ? "session" : "sessions"}
                  </span>
                </div>
              }
            />
          );
        },
      },
      colorAxis: {
        min: 0,
        minColor: "#dbeeff",
        maxColor: "#2f89df",
      },
      series: [
        {
          type: "map",
          name: "Chats",
          mapData: worldMap,
          joinBy: "hc-key",
          data,
          nullColor: "#ffffff",
          borderColor: "#c8c8c8",
          borderWidth: 1,
          states: {
            hover: {
              color: "#1F87E5",
              borderWidth: 1,
              borderColor: "#1A2023",
            },
            inactive: {
              opacity: 1,
            },
          },
          point: {
            events: {
              mouseOver() {
                const countryDetails = extractCountryDetailsFromPoint(this);

                if (!countryDetails) {
                  return;
                }

                setActiveCountryKey(countryDetails.countryKey);
              },
              mouseOut() {
                setActiveCountryKey(null);
              },
            },
          },
          dataLabels: {
            enabled: false,
          },
        },
      ],
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
    }),
    [data, isMobile]
  );

  const syncTableRowsFromChart = useCallback((chartArg?: Highcharts.Chart) => {
    const chart = chartArg ?? chartRef.current?.chart;

    if (!chart) {
      return;
    }

    const series = chart.series[0];

    if (!series || series.type !== "map") {
      return;
    }

    const rows = series.points.reduce<CountryDetails[]>((acc, point) => {
      const { value } = point as Highcharts.Point & { value?: number };
      if (typeof value !== "number" || value <= 0) {
        return acc;
      }

      const countryDetails = extractCountryDetailsFromPoint(point);

      if (!countryDetails) {
        return acc;
      }

      return [...acc, countryDetails];
    }, []);

    const sorted = rows.sort((a, b) => b.chatsCount - a.chatsCount);

    setTableRows(sorted);
  }, []);

  const onChartCreated = useCallback(
    (chart: Highcharts.Chart) => {
      syncTableRowsFromChart(chart);
    },
    [syncTableRowsFromChart]
  );

  const highlightCountry = useCallback(() => {
    if (!chartRef.current?.chart) {
      return;
    }

    const { chart } = chartRef.current;
    const series = chart.series[0];

    if (!series || series.type !== "map") {
      return;
    }

    series.points.forEach((point) => {
      const maybeHcKey = (point as unknown as Record<string, unknown>)[
        "hc-key"
      ];

      point.setState(maybeHcKey === activeCountryKey ? "hover" : "");
    });
  }, [activeCountryKey]);

  useEffect(() => {
    syncTableRowsFromChart();
  }, [syncTableRowsFromChart, data]);

  useEffect(() => {
    highlightCountry();
  }, [highlightCountry]);

  const isEmpty = tableRows.length === 0;

  return {
    chartRef,
    chartCallback: onChartCreated,
    options,
    tableRows,
    activeCountryKey,
    setActiveCountryKey,
    isEmpty,
  };
};
